
import React, { useState, useMemo, useEffect } from 'react';
import { AppState, BetOutcome, Bet } from '../types.ts';
import { SwatchBook, Calendar, Filter, ChevronDown, Activity, Edit2, PlusCircle, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import AddBetModal from './AddBetModal.tsx';

interface BetHistoryProps {
  state: AppState;
  onUpdateOutcome: (betId: string, outcome: BetOutcome) => void;
  onEditBet: (betId: string, updatedBet: Partial<Bet>) => void;
  onAddBet: (bet: any) => void;
}

const ITEMS_PER_PAGE = 5;

const BetHistory: React.FC<BetHistoryProps> = ({ state, onUpdateOutcome, onEditBet, onAddBet }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isNewBetModalOpen, setIsNewBetModalOpen] = useState(false);
  const [editingBet, setEditingBet] = useState<Bet | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [outcomeFilter, setOutcomeFilter] = useState<BetOutcome | 'ALL'>('ALL');

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [startDate, endDate, outcomeFilter, searchTerm]);

  const filteredBets = useMemo(() => {
    return state.bets.filter(bet => {
      const matchesDate = bet.date >= startDate && bet.date <= endDate;
      const matchesOutcome = outcomeFilter === 'ALL' || bet.outcome === outcomeFilter;

      const p1Name = state.players.find(p => p.id === bet.player1_id)?.name?.toLowerCase() || '';
      const p2Name = state.players.find(p => p.id === bet.player2_id)?.name?.toLowerCase() || '';
      const matchesSearch = p1Name.includes(searchTerm.toLowerCase()) || p2Name.includes(searchTerm.toLowerCase());

      return matchesDate && matchesOutcome && matchesSearch;
    });
  }, [state.bets, state.players, startDate, endDate, outcomeFilter, searchTerm]);

  const totalPages = Math.ceil(filteredBets.length / ITEMS_PER_PAGE);

  const currentBets = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredBets.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredBets, currentPage]);

  const formatDateDisplay = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  return (
    <div className="space-y-6 flex flex-col min-h-full">
      <div className="bg-[#1e2329] p-6 rounded-[24px] border border-[#474d57] shadow-xl">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="bg-[#00ff66]/10 p-3 rounded-2xl shadow-[0_0_15px_rgba(0,255,102,0.1)]">
              <SwatchBook size={24} className="text-[#00ff66]" />
            </div>
            <div>
              <h3 className="text-xl font-black uppercase tracking-tight text-white">Registro de Operações</h3>
              <p className="text-[10px] text-white font-black uppercase tracking-widest">
                Filtro Ativo: <span className="text-[#00ff66]">{filteredBets.length} Entradas</span>
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            {/* Search Bar */}
            <div className="relative flex-grow md:flex-grow-0 min-w-[200px]">
              <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                <Search size={16} className="text-[#474d57] group-focus-within:text-[#00ff66] transition-colors" />
              </div>
              <input
                type="text"
                placeholder="BUSCAR PLAYER..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-[#0b0e11] border border-[#474d57] pl-11 pr-4 py-3 rounded-2xl text-white text-xs font-black tracking-widest outline-none focus:border-[#00ff66]/50 transition-all placeholder:text-[#474d57] uppercase"
              />
            </div>

            <div className="flex items-center gap-3 bg-[#0b0e11] border border-[#474d57] px-5 py-3 rounded-2xl shadow-lg relative overflow-hidden group hover:border-[#fbbf24]/50 transition-all">
              <label
                className="flex items-center gap-2.5 relative h-6 min-w-[100px] justify-center cursor-pointer"
                onClick={(e) => (e.currentTarget.querySelector('input') as HTMLInputElement)?.showPicker?.()}
              >
                <Calendar size={16} className="text-[#fbbf24] pointer-events-none" />
                <span className="text-white text-[11px] font-black tracking-widest pointer-events-none">{formatDateDisplay(startDate)}</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                />
              </label>
              <span className="text-white text-[10px] font-black uppercase tracking-widest opacity-60 pointer-events-none">até</span>
              <label
                className="flex items-center gap-2.5 relative h-6 min-w-[100px] justify-center cursor-pointer"
                onClick={(e) => (e.currentTarget.querySelector('input') as HTMLInputElement)?.showPicker?.()}
              >
                <Calendar size={16} className="text-[#fbbf24] pointer-events-none" />
                <span className="text-white text-[11px] font-black tracking-widest pointer-events-none">{formatDateDisplay(endDate)}</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                />
              </label>
            </div>
            <button
              onClick={() => setIsNewBetModalOpen(true)}
              className="bg-[#00ff66] text-[#0b0e11] font-black px-5 py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-[#0bb574] transition-all shadow-[0_0_15px_rgba(0,255,102,0.3)] uppercase text-[10px] tracking-widest"
            >
              <PlusCircle size={16} /> Nova Entrada
            </button>
          </div>
        </div>
      </div>

      <div className="bg-[#1e2329] rounded-[32px] border border-[#474d57] overflow-hidden shadow-2xl flex flex-col">
        <div className="overflow-x-auto scrollbar-hide">
          <table className="w-full text-left border-collapse min-w-[800px]">
            <thead className="bg-[#2b3139] text-white uppercase text-[10px] font-black tracking-[0.2em] sticky top-0 z-10">
              <tr>
                <th className="px-6 py-6 border-b border-[#474d57]/30">Data</th>
                <th className="px-6 py-6 border-b border-[#474d57]/30">Confronto / Liga</th>
                <th className="px-6 py-6 text-center border-b border-[#474d57]/30">Mercado</th>
                <th className="px-6 py-6 text-right border-b border-[#474d57]/30">Stake / Odd</th>
                <th className="px-6 py-6 text-center border-b border-[#474d57]/30">Status</th>
                <th className="px-6 py-6 text-right border-b border-[#474d57]/30">Ação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#474d57]/20">
              {currentBets.length > 0 ? currentBets.map((bet) => {
                const p1 = state.players.find(p => p.id === bet.player1_id)?.name || 'P1';
                const p2 = state.players.find(p => p.id === bet.player2_id)?.name || 'P2';
                const leagueName = state.leagues.find(l => l.id === bet.league_id)?.name || 'Liga';
                const market = state.markets.find(m => m.id === bet.market_id)?.name;
                const hasScore = bet.player1_goals != null && bet.player2_goals != null;

                const [year, month, day] = bet.date.split('-');
                const formattedDate = `${day}/${month}/${year}`;

                return (
                  <tr key={bet.id} className="hover:bg-[#2b3139]/40 transition-colors group">
                    <td className="px-6 py-8 text-white font-mono text-sm font-black">
                      {formattedDate}
                    </td>
                    <td className="px-6 py-8">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-3">
                          <span className="font-black text-white text-lg group-hover:text-[#00ff66] transition-colors uppercase whitespace-nowrap">{p1}</span>
                          {hasScore ? (
                            <div className="flex items-center gap-1.5 px-3 py-1 bg-[#0b0e11] rounded-lg border border-[#00ff66]/30 font-mono font-black text-[#00ff66] shadow-[0_0_10px_rgba(0,255,102,0.1)] text-sm">
                              <span>{bet.player1_goals}</span>
                              <span className="opacity-30">-</span>
                              <span>{bet.player2_goals}</span>
                            </div>
                          ) : (
                            <span className="text-xs font-black uppercase text-[#fbbf24]">VS</span>
                          )}
                          <span className="font-black text-white text-lg group-hover:text-[#00ff66] transition-colors uppercase whitespace-nowrap">{p2}</span>
                        </div>
                        <div className="text-[10px] font-black text-[#fbbf24] uppercase tracking-widest mt-0.5 opacity-90 truncate max-w-[250px]">
                          ({leagueName})
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-8 text-center font-black text-xs uppercase tracking-widest text-white whitespace-nowrap">
                      {market}
                    </td>
                    <td className="px-6 py-8 text-right">
                      <div className="font-black text-white text-sm font-mono">$ {bet.stake.toFixed(2)}</div>
                      <div className="text-[10px] text-[#fbbf24] font-black mt-0.5">@ {bet.odd.toFixed(2)}</div>
                    </td>
                    <td className="px-6 py-8 text-center">
                      <select value={bet.outcome} onChange={(e) => onUpdateOutcome(bet.id, e.target.value as BetOutcome)} className={`text-[10px] font-black px-3 py-2 rounded-xl bg-[#0b0e11] border border-[#474d57] outline-none cursor-pointer uppercase tracking-widest ${bet.outcome === BetOutcome.GREEN ? 'text-[#00ff66]' : bet.outcome === BetOutcome.RED ? 'text-[#f6465d]' : 'text-white'}`}>
                        <option value={BetOutcome.PENDING} className="bg-[#1e2329]">Pendente</option>
                        <option value={BetOutcome.GREEN} className="bg-[#1e2329]">Green</option>
                        <option value={BetOutcome.RED} className="bg-[#1e2329]">Red</option>
                        <option value={BetOutcome.REFUND} className="bg-[#1e2329]">Push</option>
                      </select>
                    </td>
                    <td className="px-6 py-8 text-right">
                      <button
                        onClick={() => setEditingBet(bet)}
                        className="p-2 rounded-xl bg-[#2b3139] border border-[#474d57] text-[#00ff66] hover:bg-[#00ff66] hover:text-[#0b0e11] transition-all hover:shadow-[0_0_10px_#00ff66]"
                      >
                        <Edit2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={6} className="px-6 py-20 text-center text-[#b1b5c0] font-black uppercase tracking-widest opacity-40 text-xs">
                    Nenhuma operação encontrada
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-[#1e2329] px-8 py-6 border-t border-[#474d57]/30 flex items-center justify-between mt-auto">
            <div className="text-[10px] text-white font-black uppercase tracking-widest opacity-60">
              Página <span className="text-[#00ff66]">{currentPage}</span> de {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-xl bg-[#2b3139] border border-[#474d57] text-white disabled:opacity-20 disabled:cursor-not-allowed hover:border-[#00ff66]/50 transition-all"
              >
                <ChevronLeft size={18} />
              </button>

              <div className="flex items-center gap-1 mx-2">
                {[...Array(totalPages)].map((_, i) => {
                  const pageNum = i + 1;
                  // Show current page, first, last, and relative pages
                  if (
                    pageNum === 1 ||
                    pageNum === totalPages ||
                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`w-8 h-8 rounded-lg text-[10px] font-black transition-all ${currentPage === pageNum
                          ? 'bg-[#00ff66] text-[#0b0e11]'
                          : 'bg-[#2b3139] text-white border border-[#474d57] hover:border-[#00ff66]/50'
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (
                    pageNum === currentPage - 2 ||
                    pageNum === currentPage + 2
                  ) {
                    return <span key={pageNum} className="text-white opacity-20 mx-0.5">...</span>;
                  }
                  return null;
                })}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-xl bg-[#2b3139] border border-[#474d57] text-white disabled:opacity-20 disabled:cursor-not-allowed hover:border-[#00ff66]/50 transition-all"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

      <AddBetModal
        isOpen={isNewBetModalOpen || !!editingBet}
        onClose={() => { setIsNewBetModalOpen(false); setEditingBet(null); }}
        onAdd={editingBet ? (updated) => onEditBet(editingBet.id, updated) : onAddBet}
        state={state}
        initialData={editingBet}
      />
    </div>
  );
};

export default BetHistory;
